import { problemDetailsSchema, type ProblemDetails } from '@dashboard/contracts';

/**
 * The single place the browser talks to the API.
 *
 * Every request is same-origin and relative, which is what makes the cookie
 * session work without CORS. Every failure becomes an ApiError carrying the
 * machine-readable code, so callers branch on `error.code` and the UI layer
 * translates it -- no component ever parses an error message (section 16).
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly params?: Record<string, unknown>;
  readonly correlationId?: string;
  readonly fieldErrors?: ProblemDetails['errors'];

  constructor(problem: Partial<ProblemDetails> & { status: number; code: string }) {
    super(problem.code);
    this.name = 'ApiError';
    this.status = problem.status;
    this.code = problem.code;
    this.params = problem.params;
    this.correlationId = problem.correlationId;
    this.fieldErrors = problem.errors;
  }

  get isAuthError(): boolean {
    return this.status === 401 || this.code === 'auth.session_expired';
  }

  get isPermissionError(): boolean {
    return this.status === 403 && this.code !== 'auth.csrf_invalid';
  }
}

/**
 * The CSRF token is held in module scope rather than a cookie or storage: it is
 * read from the session context on boot and refreshed whenever the context is
 * refetched. Nothing sensitive is persisted client-side (section 12.3).
 */
let csrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Passed through from the query layer so navigating away cancels in flight work. */
  signal?: AbortSignal;
  query?: Record<string, string | number | boolean | string[] | undefined>;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const url = withQuery(path, options.query);

  const headers: Record<string, string> = { accept: 'application/json' };
  if (options.body !== undefined) headers['content-type'] = 'application/json';
  if (MUTATING_METHODS.has(method) && csrfToken) headers['x-csrf-token'] = csrfToken;

  const response = await fetch(url, {
    method,
    headers,
    // Same-origin, so the session cookie rides along without credentials mode
    // needing to be 'include' and without a preflight.
    credentials: 'same-origin',
    signal: options.signal,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });

  if (response.status === 204) return undefined as T;

  if (!response.ok) throw await toApiError(response);

  return (await response.json()) as T;
}

export interface UploadOptions {
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
}

/**
 * `apiRequest`'s sibling for the one route that does not take JSON: an
 * asset upload sends the exact bytes of a file, not a JSON-encoded copy of
 * them (docs/rich-text-design.md section 5.3) -- mirroring the raw-body
 * shape `POST /api/v1/assets` itself is built on. `content-type` is the
 * file's own, so the server sniffs and validates the same bytes a checksum
 * will later be computed over.
 */
export async function apiUpload<T>(
  path: string,
  file: Blob,
  options: UploadOptions = {},
): Promise<T> {
  const url = withQuery(path, options.query);

  const headers: Record<string, string> = {
    accept: 'application/json',
    'content-type': file.type || 'application/octet-stream',
  };
  if (csrfToken) headers['x-csrf-token'] = csrfToken;

  const response = await fetch(url, {
    method: 'POST',
    headers,
    credentials: 'same-origin',
    signal: options.signal,
    body: file,
  });

  if (!response.ok) throw await toApiError(response);

  return (await response.json()) as T;
}

function withQuery(
  path: string,
  query: Record<string, string | number | boolean | string[] | undefined> | undefined,
): string {
  const url = new URL(path, window.location.origin);

  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined) continue;
    if (Array.isArray(value)) {
      for (const entry of value) url.searchParams.append(key, entry);
    } else {
      url.searchParams.set(key, String(value));
    }
  }

  return url.toString();
}

async function toApiError(response: Response): Promise<ApiError> {
  try {
    const parsed = problemDetailsSchema.partial().parse(await response.json());
    return new ApiError({
      ...parsed,
      status: parsed.status ?? response.status,
      code: parsed.code ?? 'internal.error',
    });
  } catch {
    // A non-JSON failure means something before the application answered: a
    // proxy, a gateway timeout, an offline browser.
    return new ApiError({ status: response.status, code: 'internal.error' });
  }
}
